import { Card } from "@/components/ui/card";
import { IMediaItem, IPost } from "@/lib/community";
import { useEffect, useMemo, useState } from "react";
import { formatDate } from "@/utils/formatDate";
import { Link } from "react-router";
import { cn } from "@/lib/utils";
import { Eye, PinIcon, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MixpanelActions } from "@/integrations/mixpanel/mixpanel";
import ProfilePopup from "../ui/ProfilePopup";
import { useTabNavigation } from "@/stores/use-nav-store";
import CustomAvatar from "../community/CustomAvatar";

interface IProps {
  post: IPost;
  canBePined?: boolean;
  onClick?: () => void;
  onDelete?: (postId: string) => void;
}

const getYoutubeId = (url) => {
  const match = url.match(/embed\/([^?&/]+)/);
  return match ? match[1] : null;
};

export default function PostCard({ post, onClick, onDelete }: IProps) {
  const { t } = useTranslation();
  const { isLargeScreen } = useTabNavigation(768);
  const [hovered, setHovered] = useState(false);
  const [firstMedia, setFirstMedia] = useState<IMediaItem | null>();

  useEffect(() => {
    try {
      const raw = post.media?.at(0);
      if (raw && typeof raw === "string" && raw.trim().startsWith("{")) {
        setFirstMedia(JSON.parse(raw));
      } else {
        setFirstMedia(null);
      }
    } catch (err) {
      setFirstMedia(null);
    }
  }, [post.media]);

  const fullDate = useMemo(() => {
    const date = new Date(post.created_at);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, [post.created_at]);

  const showPin = false;
  return (
    <Card
      onClick={onClick}
      className={cn(
        "mx-auto w-full sm:w-[94%] lg:w-full p-4 shadow-lg border hover:shadow-xl cursor-pointer transition group bg-background text-foreground"
      )}
    >
      <div className="min-w-0">
        <div className="flex justify-between">
          <div
            className="flex gap-4"
            onMouseEnter={() => {
              setHovered(true);
            }}
            onMouseLeave={() => {
              setHovered(false);
            }}
          >
            <div className="relative">
              <Link
                to={`/user/${post.author}`}
                onClick={(e) => {
                  MixpanelActions.clickUserProfile();
                  e.stopPropagation();
                }}
              >
                <CustomAvatar userData={post.authorData} />
                {hovered && (
                  <ProfilePopup
                    authorData={post.authorData}
                    authorId={post.author}
                  />
                )}
              </Link>
            </div>
            <div className="">
              <Link
                to={`/user/${post.author}`}
                onClick={(e) => {
                  MixpanelActions.clickUserProfile();
                  e.stopPropagation();
                }}
              >
                <div className="font-semibold text-base">
                  {post.authorData.username}
                </div>
              </Link>
              <div className="text-xs text-gray-400 relative group/tooltip">
                {formatDate(post.created_at, t)}
                <div className="absolute bottom-full left-0 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all duration-200 whitespace-nowrap z-10">
                  {t("community.postCard.postCreated", { date: fullDate })}
                </div>
              </div>
            </div>
          </div>
          {showPin && (
            <div className="flex gap-1 items-center">
              <PinIcon size={18} className="rotate-45" fill="#eab308" />
              <span className="font-semibold">
                {t("community.postCard.pinned")}
              </span>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-row gap-4">
          <div className="flex-1 min-w-0 flex flex-col items-start justify-between">
            <div>
              <div className="text-xl font-bold text-foreground mb-1 line-clamp-1">
                {post.title}
              </div>
              <div className="text-gray-700 dark:text-white/80 text-sm line-clamp-2">
                {post.content.length > 160
                  ? post.content.slice(0, 160) + "..."
                  : post.content}
              </div>
            </div>
            {!post.is_hidden && (
              <div className="flex gap-2 mt-4 self-start">
                <button
                  className="text-red-500 text-sm hover:text-red-700 flex items-center gap-1"
                  onClick={() => onDelete(post.id)}
                >
                  <Trash2 size={14} />
                  Delete
                </button>
                <Link to={`/post/${post.id}`}>
                  <button className="text-blue-500 text-sm hover:text-blue-700 flex items-center gap-1">
                    <Eye size={14} />
                    See
                  </button>
                </Link>
              </div>
            )}
          </div>

          {firstMedia && (
            <div className="w-28 h-28 rounded-xl overflow-hidden flex-shrink-0 border border-border shadow-inner">
              {firstMedia.type === "image" ? (
                <img
                  src={firstMedia.url}
                  alt="media"
                  className="w-full h-full object-cover"
                />
              ) : firstMedia?.url.includes("embed") ? (
                (() => {
                  const videoId = getYoutubeId(firstMedia.url);
                  return videoId ? (
                    <div className="relative w-full h-full bg-black">
                      <img
                        src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
                        alt="video preview"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <svg
                          width="48"
                          height="48"
                          viewBox="0 0 48 48"
                          className="opacity-80"
                        >
                          <circle
                            cx="24"
                            cy="24"
                            r="24"
                            fill="black"
                            fillOpacity="0.5"
                          />
                          <polygon points="20,16 36,24 20,32" fill="white" />
                        </svg>
                      </div>
                    </div>
                  ) : null;
                })()
              ) : (
                <video
                  src={firstMedia.url}
                  className="w-full h-full object-cover"
                  controls
                />
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
